import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Database, User, Settings, ArrowRight, ArrowLeft, Loader2, AlertCircle, Star, Globe, Server, HelpCircle, ChevronDown, ChevronUp, ExternalLink, Upload, FileJson, Code2, Copy, Check } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const HostingGuide = () => {
  const [open, setOpen] = useState(false);
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${isLocalhost ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
      <button
        className="w-full flex items-center justify-between p-4 text-left"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center space-x-3">
          <span className="text-lg">{isLocalhost ? '⚠️' : '📋'}</span>
          <div>
            <p className="font-semibold text-gray-900 text-sm">
              {isLocalhost
                ? 'This is running on your computer — need to put it on the web first?'
                : 'How to install MorphBook on a web hosting service'}
            </p>
            <p className="text-xs text-gray-500">Step-by-step guide for Hostinger, Bluehost, and similar hosts — click to expand</p>
          </div>
        </div>
        <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200 pt-4">
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <p className="text-xs font-bold text-gray-800 mb-2">Before you continue — what is web hosting?</p>
            <p className="text-xs text-gray-600">
              Web hosting is a service that keeps your booking system running 24/7 on the internet so your clients can reach it from anywhere. Think of it like renting space on a computer that's always on. Popular options include <strong>Hostinger</strong>, <strong>Bluehost</strong>, and <strong>SiteGround</strong>.
            </p>
            <p className="text-xs text-gray-500 mt-2 italic">The steps below use Hostinger as the example, but the process is very similar on other hosting providers.</p>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-bold text-gray-800 uppercase tracking-wide">Step-by-step: Installing MorphBook on Hostinger</p>

            <div className="flex items-start space-x-3 text-xs text-gray-700">
              <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
              <div><strong>Sign up and log in</strong> at <span className="text-primary-600 font-medium">hostinger.com</span>. Purchase a hosting plan and register a domain name (e.g. <code>yourbusiness.com</code>) — your domain is the address clients will use to find you. Once logged in, click on your website from the main dashboard to open the management area.</div>
            </div>

            <div className="flex items-start space-x-3 text-xs text-gray-700">
              <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
              <div>
                <strong>Create a subdomain for your booking system</strong> — a subdomain gives the booking system its own web address separate from your main site (e.g. <code>booking.yourbusiness.com</code>).
                <div className="mt-1.5 bg-gray-100 rounded p-2 space-y-0.5">
                  <p>→ In your Hostinger management area, click <strong>Domains</strong> in the left menu, then <strong>Subdomains</strong></p>
                  <p>→ Type <code>booking</code> in the subdomain field — this creates the address <code>booking.yourdomain.com</code></p>
                  <p>→ Hostinger will automatically create a folder with the same name to hold your files</p>
                  <p>→ Click <strong>Create</strong></p>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3 text-xs text-gray-700">
              <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
              <div>
                <strong>Create a MySQL database</strong> — MorphBook needs a database to store your bookings and client info.
                <div className="mt-1.5 bg-gray-100 rounded p-2 space-y-0.5">
                  <p>→ In your Hostinger management area, click <strong>Databases</strong> in the left menu, then click <strong>Management</strong></p>
                  <p>→ Fill in a database name, username, and password, then click <strong>Create</strong></p>
                  <p>→ <strong>Write these three things down</strong> — you'll need them in step 5</p>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3 text-xs text-gray-700">
              <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
              <div>
                <strong>Upload the two MorphBook files</strong> — when you downloaded MorphBook, you downloaded two separate files: <code>install.php</code> and <code>dog-grooming-system-v1.0.0.tar.gz</code>. Both should be in your <strong>Downloads</strong> folder. You only need these two files — do not extract or open them.
                <div className="mt-1.5 bg-gray-100 rounded p-2 space-y-0.5">
                  <p>→ In your Hostinger management area, click <strong>Files</strong> in the left menu, then <strong>File Manager</strong></p>
                  <p>→ You'll see a folder called <strong>public_html</strong> — double-click it to open it, then double-click the <strong>booking</strong> folder inside (or whatever name you gave your subdomain in step 2)</p>
                  <p>→ Look for the <strong>upload icon</strong> (an upward arrow ↑) in the <strong>top-right corner</strong> of the file manager and click it</p>
                  <p>→ In the popup that appears, click <strong>File</strong>, then select both files from your Downloads folder</p>
                  <p>→ Upload <code>install.php</code> first, then repeat to upload <code>dog-grooming-system-v1.0.0.tar.gz</code></p>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3 text-xs text-gray-700">
              <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">5</span>
              <div>
                <strong>Run the installer</strong> — open a new browser tab and visit:
                <div className="mt-1 bg-gray-100 rounded p-2">
                  <code>https://booking.yourdomain.com/install.php</code>
                </div>
                <p className="mt-1">Enter your site URL and the database name, username, and password you created in step 3, then click <strong>Install MorphBook</strong>.</p>
              </div>
            </div>

            <div className="flex items-start space-x-3 text-xs text-gray-700">
              <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">6</span>
              <div><strong>Complete this setup wizard</strong> — the installer automatically brings you back to this page. Continue through the steps below to create your admin account and configure your system. You're almost done!</div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800"><strong>Already on a web host and seeing this at your real domain?</strong> You're in the right place — just scroll past this and continue the setup below!</p>
          </div>
        </div>
      )}
    </div>
  );
};

const EmbedStep = () => {
  const siteUrl = window.location.origin;
  const [copied, setCopied] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const iframeSnippet = `<iframe\n  src="${siteUrl}/book"\n  width="100%"\n  height="900"\n  frameborder="0"\n  style="border-radius:12px;"\n></iframe>`;

  const [widgetPosition, setWidgetPosition] = useState('bottom-right');
  const widgetSnippet = `<script>\n  window.MorphBookConfig = { url: '${siteUrl}', buttonText: 'Book Now', position: '${widgetPosition}' };\n</script>\n<script src="${siteUrl}/widget.js"></script>`;

  const CodeBlock = ({ code, id }) => (
    <div className="relative bg-gray-900 rounded-lg p-4 text-left">
      <pre className="text-green-400 text-xs overflow-x-auto whitespace-pre-wrap pr-8">{code}</pre>
      <button
        onClick={() => copy(code, id)}
        className="absolute top-2 right-2 p-1.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors"
        title="Copy"
      >
        {copied === id ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );

  const Step = ({ n, text }) => (
    <div className="flex items-start space-x-2 text-sm text-gray-700">
      <span className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{n}</span>
      <span>{text}</span>
    </div>
  );

  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Add Booking to Your Website</h2>
        <p className="text-gray-600 mt-1 text-sm">
          Choose one of the options below to let clients book directly from your existing website.
          Not sure? The simplest option is just to share your booking link.
        </p>
      </div>

      {/* Localhost warning banner */}
      {isLocalhost && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 space-y-2">
          <p className="text-sm font-bold text-amber-900">⚠️ You're running this on your own computer — your clients can't see it yet</p>
          <p className="text-sm text-amber-800">
            Right now MorphBook is only accessible on <em>your</em> machine. Before you can share it with clients or add it to your website, you need to upload it to a <strong>web hosting service</strong> — this is like renting space on the internet so your booking system has a real web address.
          </p>
          <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-1.5 text-xs text-amber-900">
            <p className="font-semibold">What to do next:</p>
            <p>1. Sign up for a web host — <strong>Hostinger</strong>, Bluehost, and SiteGround are popular options.</p>
            <p>2. In your hosting account, find the <strong>File Manager</strong> and upload the two MorphBook files (<code>install.php</code> and the <code>.tar.gz</code> file) to your <code>public_html</code> folder.</p>
            <p>3. Visit <code>yourdomain.com/install.php</code> in a browser and follow the same setup steps you just completed here.</p>
            <p>4. Once installed on your host, come back to this step — your booking link will show your real web address instead of <code>localhost:9000</code>.</p>
          </div>
          <p className="text-xs text-amber-700 font-medium">The codes below use <code>localhost:9000</code> which only works on this computer. They will automatically show your real address once you're on a web host.</p>
        </div>
      )}

      {/* Simplest: just share the link */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-green-800 mb-1">✅ Simplest — share your booking link directly</p>
        <p className="text-xs text-green-700 mb-2">Copy this link and add it to your website as a button or menu item, or text it to clients:</p>
        <div className="flex items-center space-x-2">
          <code className="flex-1 bg-white border border-green-300 rounded px-3 py-1.5 text-xs text-gray-800 truncate">{siteUrl}/book</code>
          <button
            onClick={() => copy(`${siteUrl}/book`, 'link')}
            className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors flex-shrink-0"
          >
            {copied === 'link' ? <Check size={12} /> : <Copy size={12} />}
            <span>{copied === 'link' ? 'Copied!' : 'Copy link'}</span>
          </button>
        </div>
      </div>

      {/* Option 1: iFrame embed */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          onClick={() => setExpanded(expanded === 'iframe' ? null : 'iframe')}
        >
          <div className="flex items-center space-x-3">
            <span className="w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Embed booking form inside a page</p>
              <p className="text-xs text-gray-500">Works on WordPress, Wix, Squarespace, Webflow, and most website builders</p>
            </div>
          </div>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${expanded === 'iframe' ? 'rotate-180' : ''}`} />
        </button>
        {expanded === 'iframe' && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
            <div className="bg-blue-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-blue-800">How to add this to WordPress:</p>
              <Step n="1" text='In your WordPress dashboard, go to Pages and open or create the page where you want booking to appear (e.g. "Book Now").' />
              <Step n="2" text='Click the "+" button to add a new block, then type "HTML" in the search box and select the "Custom HTML" block that appears.' />
              <Step n="3" text="Copy the code below and paste it into that block." />
              <Step n="4" text='Click "Update" or "Publish" to save. Your booking form will now appear on that page.' />
            </div>
            <div className="bg-blue-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-blue-800">How to add this to Wix:</p>
              <Step n="1" text='In the Wix Editor, click "+" → Embed → HTML iFrame.' />
              <Step n="2" text='Click "Enter Code", paste the code below, then click "Update".' />
            </div>
            <p className="text-xs font-semibold text-gray-700">Your embed code:</p>
            <CodeBlock code={iframeSnippet} id="iframe" />
          </div>
        )}
      </div>

      {/* Option 2: Floating widget */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          onClick={() => setExpanded(expanded === 'widget' ? null : 'widget')}
        >
          <div className="flex items-center space-x-3">
            <span className="w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Floating "Book Now" button on every page</p>
              <p className="text-xs text-gray-500">A button floats in the corner of every page on your site — clients click it to open booking</p>
            </div>
          </div>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${expanded === 'widget' ? 'rotate-180' : ''}`} />
        </button>
        {expanded === 'widget' && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
            <div className="bg-blue-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-blue-800">How to add this to WordPress (recommended method):</p>
              <Step n="1" text='In your WordPress dashboard, go to Plugins → Add New. Search for "Insert Headers and Footers" — install the one by WPCode (it will say "WPCode" under the plugin name). Do not confuse it with other similarly named plugins.' />
              <Step n="2" text='Once activated, go to Settings → Insert Headers and Footers.' />
              <Step n="3" text='Paste the widget code below into the "Scripts in Footer" box.' />
              <Step n="4" text='Click Save. Visit your website — a Book Now button will appear on every page.' />
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800"><strong>Why a plugin?</strong> Most modern WordPress themes (including Elementor and Hello Elementor) do not have a built-in area to add custom scripts. This free plugin is the standard way to do it safely without editing theme files.</p>
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-700">Button position:</p>
              <div className="flex flex-wrap gap-2">
                {[['bottom-right','Bottom Right'],['bottom-center','Bottom Center'],['bottom-left','Bottom Left'],['top-right','Top Right'],['top-center','Top Center'],['top-left','Top Left']].map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setWidgetPosition(val)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      widgetPosition === val
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                    }`}
                  >{label}</button>
                ))}
              </div>
            </div>
            <p className="text-xs font-semibold text-gray-700">Your widget code:</p>
            <CodeBlock code={widgetSnippet} id="widget" />
          </div>
        )}
      </div>

      {/* Option 3: Manual button on a page */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          onClick={() => setExpanded(expanded === 'button' ? null : 'button')}
        >
          <div className="flex items-center space-x-3">
            <span className="w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Place a "Book Now" button on a specific page yourself</p>
              <p className="text-xs text-gray-500">You design and place the button exactly where you want it on one page</p>
            </div>
          </div>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${expanded === 'button' ? 'rotate-180' : ''}`} />
        </button>
        {expanded === 'button' && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
            <div className="bg-blue-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-blue-800">How to add a booking button in WordPress:</p>
              <Step n="1" text='In your WordPress dashboard, go to Pages and open or create the page where you want the button (e.g. "Book Now" or your home page).' />
              <Step n="2" text='Click the "+" to add a new block, type "Button" in the search box, and select the Button block.' />
              <Step n="3" text={`Type your button label (e.g. "Book Now"), then click the button and set its link to: ${siteUrl}/book`} />
              <Step n="4" text='Use the block settings on the right to change the button colour, size, and alignment to match your site.' />
              <Step n="5" text='Click "Update" or "Publish" to save. The button will now appear on that page and take clients straight to booking when clicked.' />
            </div>
            <div className="bg-blue-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-blue-800">How to add a booking button in Wix:</p>
              <Step n="1" text='In the Wix Editor, click "+" → Button and drag it onto the page where you want it.' />
              <Step n="2" text={`Click the button, then click "Link" and paste in your booking address: ${siteUrl}/book`} />
              <Step n="3" text='Style the button colour and text to match your site, then click Publish.' />
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-800"><strong>Your booking address:</strong> <code className="bg-white px-1 py-0.5 rounded">{siteUrl}/book</code> — set this as the link destination for any button.</p>
            </div>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-gray-400">You can find these options again anytime in Admin → Settings → Embed.</p>
    </div>
  );
};

const SetupWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Database configuration
  const [dbType, setDbType] = useState('sqlite');
  const [dbConfig, setDbConfig] = useState({
    host: 'localhost',
    port: '',
    database: 'dog_grooming',
    user: '',
    password: ''
  });
  const [dbOptions, setDbOptions] = useState([]);
  const [connectionTested, setConnectionTested] = useState(false);
  const [tablesCreated, setTablesCreated] = useState(false);
  const [showHostingHelp, setShowHostingHelp] = useState(false);
  const [selectedHost, setSelectedHost] = useState('hostinger');
  
  // Admin account
  const [adminData, setAdminData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    businessName: ''
  });
  
  // Config import (step 2)
  const [configFile, setConfigFile] = useState(null);
  const [configImported, setConfigImported] = useState(false);
  const [configImportLoading, setConfigImportLoading] = useState(false);
  const [configImportResult, setConfigImportResult] = useState(null);

  // Client import
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [importSkipped, setImportSkipped] = useState(false);
  const [csvColumns, setCsvColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [showMappingUI, setShowMappingUI] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState([]);

  // MorphBook fields that can be mapped to
  const morphBookFields = [
    { id: 'client_first_name', label: 'Client First Name', category: 'client' },
    { id: 'client_last_name', label: 'Client Last Name', category: 'client' },
    { id: 'client_email', label: 'Client Email', category: 'client' },
    { id: 'client_phone', label: 'Client Phone', category: 'client' },
    { id: 'client_address', label: 'Client Address', category: 'client' },
    { id: 'pet_name', label: 'Pet Name', category: 'pet' },
    { id: 'pet_breed', label: 'Pet Breed', category: 'pet' },
    { id: 'pet_size', label: 'Pet Size', category: 'pet' },
    { id: 'pet_age', label: 'Pet Age', category: 'pet' },
    { id: 'pet_weight', label: 'Pet Weight', category: 'pet' },
    { id: 'pet_notes', label: 'Pet Notes/Medical', category: 'pet' },
    { id: 'notes', label: 'General Notes (catch-all)', category: 'other' },
    { id: 'skip', label: "Don't import this column", category: 'other' },
  ];

  // Store all parsed CSV data
  const [allCsvData, setAllCsvData] = useState([]);

  // Parse CSV file when uploaded
  const parseCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length > 0) {
        // Get headers from first line
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        setCsvColumns(headers);
        
        // Auto-map columns based on common names
        const autoMapping = {};
        headers.forEach(header => {
          const lowerHeader = header.toLowerCase();
          if (lowerHeader.includes('first') && lowerHeader.includes('name')) autoMapping[header] = 'client_first_name';
          else if (lowerHeader.includes('last') && lowerHeader.includes('name')) autoMapping[header] = 'client_last_name';
          else if (lowerHeader === 'name' || lowerHeader === 'client name' || lowerHeader === 'customer name') autoMapping[header] = 'client_first_name';
          else if (lowerHeader.includes('email')) autoMapping[header] = 'client_email';
          else if (lowerHeader.includes('phone') || lowerHeader.includes('tel')) autoMapping[header] = 'client_phone';
          else if (lowerHeader.includes('address')) autoMapping[header] = 'client_address';
          else if ((lowerHeader.includes('pet') && lowerHeader.includes('name')) || lowerHeader === 'dog name' || lowerHeader === 'pet') autoMapping[header] = 'pet_name';
          else if (lowerHeader.includes('breed')) autoMapping[header] = 'pet_breed';
          else if (lowerHeader.includes('size')) autoMapping[header] = 'pet_size';
          else if (lowerHeader.includes('age')) autoMapping[header] = 'pet_age';
          else if (lowerHeader.includes('weight')) autoMapping[header] = 'pet_weight';
          else if (lowerHeader.includes('note') || lowerHeader.includes('comment') || lowerHeader.includes('special')) autoMapping[header] = 'notes';
          else autoMapping[header] = 'notes'; // Default unmapped to notes
        });
        setColumnMapping(autoMapping);
        
        // Parse ALL data rows
        const allRows = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const row = {};
          headers.forEach((h, i) => row[h] = values[i] || '');
          return row;
        });
        setAllCsvData(allRows);
        
        // Get preview data (first 3 rows)
        setCsvPreviewData(allRows.slice(0, 3));
        setShowMappingUI(true);
      }
    };
    reader.readAsText(file);
  };

  // Import config from JSON file
  const importConfig = async () => {
    if (!configFile) return false;
    setConfigImportLoading(true);
    setError('');
    try {
      const text = await configFile.text();
      const config = JSON.parse(text);
      const response = await axios.post(`${API_URL}/setup/import-config`, { config });
      if (response.data.success) {
        setConfigImported(true);
        setConfigImportResult(response.data);
        setSuccess('Configuration imported! Your services, pricing, and branding have been pre-loaded.');
        return true;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import config. Make sure you uploaded a valid MorphBook config file.');
      return false;
    } finally {
      setConfigImportLoading(false);
    }
  };

  // Import clients to backend
  const importClients = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/setup/import-clients`, {
        mapping: columnMapping,
        data: allCsvData
      });
      
      if (response.data.success) {
        setImportProgress({
          complete: true,
          imported: response.data.stats.clientsCreated,
          pets: response.data.stats.petsCreated,
          errors: response.data.errors || []
        });
        setSuccess(`Imported ${response.data.stats.clientsCreated} clients and ${response.data.stats.petsCreated} pets!`);
        return true;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDatabaseOptions();
  }, []);

  const loadDatabaseOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/database-options`);
      setDbOptions(response.data.options);
      setDbType(response.data.currentType || 'sqlite');
    } catch (err) {
      console.error('Failed to load database options:', err);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${API_URL}/setup/test-connection`, {
        dbType,
        ...dbConfig
      });
      
      if (response.data.success) {
        setSuccess('Connection successful!');
        setConnectionTested(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Connection failed');
      setConnectionTested(false);
    } finally {
      setLoading(false);
    }
  };

  const saveDbConfig = async () => {
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API_URL}/setup/configure-database`, {
        dbType,
        ...dbConfig
      });
      setSuccess('Database configuration saved!');
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save configuration');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const runMigrations = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/setup/run-migrations`);
      if (response.data.success) {
        setSuccess('Database tables created successfully!');
        setTablesCreated(true);
        return true;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create database tables');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createAdmin = async () => {
    setLoading(true);
    setError('');
    
    // Validation
    if (!adminData.email || !adminData.password || !adminData.firstName || !adminData.lastName) {
      setError('Please fill in all required fields');
      setLoading(false);
      return false;
    }
    
    if (adminData.password !== adminData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return false;
    }
    
    if (adminData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return false;
    }
    
    try {
      const response = await axios.post(`${API_URL}/setup/create-admin`, {
        email: adminData.email,
        password: adminData.password,
        firstName: adminData.firstName,
        lastName: adminData.lastName,
        phone: adminData.phone,
        businessName: adminData.businessName
      });
      
      if (response.data.success) {
        setSuccess('Admin account created successfully!');
        return true;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create admin account');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    setError('');
    setSuccess('');
    
    if (currentStep === 1) {
      // Database selection
      if (dbType === 'sqlite') setConnectionTested(true);
      if (!connectionTested && dbType !== 'sqlite') {
        setError('Please test the database connection first');
        return;
      }
      const saved = await saveDbConfig();
      if (saved) setCurrentStep(2);
    } else if (currentStep === 2) {
      // Config import (optional) — run migrations first, then apply config if provided
      if (!tablesCreated) {
        const created = await runMigrations();
        if (!created) return;
      }
      if (configFile && !configImported) {
        const ok = await importConfig();
        if (!ok) return;
      }
      setCurrentStep(3);
    } else if (currentStep === 3) {
      // Client CSV import (optional)
      setCurrentStep(4);
    } else if (currentStep === 4) {
      // Create admin
      const created = await createAdmin();
      if (created) setCurrentStep(5);
    } else if (currentStep === 5) {
      // Complete - go to embed step
      setCurrentStep(6);
    } else if (currentStep === 6) {
      // Embed - go to login
      window.location.href = '/login';
    }
  };

  const handleBack = () => {
    setError('');
    setSuccess('');
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const selectedDbOption = dbOptions.find(opt => opt.id === dbType);

  const steps = [
    { number: 1, title: 'Storage', icon: Database },
    { number: 2, title: 'Import Config', icon: FileJson },
    { number: 3, title: 'Your Clients', icon: User },
    { number: 4, title: 'Your Account', icon: User },
    { number: 5, title: 'Complete', icon: CheckCircle },
    { number: 6, title: 'Embed', icon: Code2 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to MorphBook</h1>
          <p className="text-gray-600">Let's set up your online booking system in just a few easy steps</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.number;
            const isComplete = currentStep > step.number;
            
            return (
              <div key={step.number} className="flex flex-col items-center flex-1">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                  isComplete ? 'bg-green-500 text-white' :
                  isActive ? 'bg-primary-600 text-white' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {isComplete ? <CheckCircle size={24} /> : <StepIcon size={24} />}
                </div>
                <span className={`text-sm font-medium ${isActive ? 'text-primary-600' : 'text-gray-500'}`}>
                  {step.title}
                </span>
                {index < steps.length - 1 && (
                  <div className={`hidden sm:block absolute h-0.5 w-16 top-6 left-1/2 ${
                    isComplete ? 'bg-green-500' : 'bg-gray-200'
                  }`} style={{ marginLeft: '2rem' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
            <AlertCircle className="mr-2 flex-shrink-0" size={20} />
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
            <CheckCircle className="mr-2 flex-shrink-0" size={20} />
            {success}
          </div>
        )}

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Database Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Where will your booking system live?</h2>
                <p className="text-gray-600 mt-2">
                  This determines where your client information and appointments are stored. Don't worry - we'll help you pick!
                </p>
              </div>

              {/* Hosting setup guide — collapsible */}
              <HostingGuide />

              {/* Help Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <HelpCircle className="text-blue-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-blue-800">
                    <strong>Not sure what to pick?</strong> If you're running this on your own computer, 
                    a personal server, or most web hosting services, <strong>SQLite</strong> is the easiest choice. 
                    It works right away with no extra setup!
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* SQLite Option */}
                <label
                  className={`block p-5 border-2 rounded-xl cursor-pointer transition-all ${
                    dbType === 'sqlite' 
                      ? 'border-primary-500 bg-primary-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="dbType"
                      value="sqlite"
                      checked={dbType === 'sqlite'}
                      onChange={(e) => {
                        setDbType(e.target.value);
                        setConnectionTested(false);
                      }}
                      className="mt-1 mr-4"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 text-lg">SQLite</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Star size={12} className="mr-1" /> Recommended
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">The simplest option - works instantly with zero configuration!</p>
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-sm font-medium text-gray-700 mb-2">Best for:</p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> Running on your own computer</li>
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> Small to medium businesses</li>
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> VPS hosting (DigitalOcean, Linode, etc.)</li>
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> Platforms like Railway, Render, or Heroku</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </label>

                {/* MySQL Option */}
                <label
                  className={`block p-5 border-2 rounded-xl cursor-pointer transition-all ${
                    dbType === 'mysql' 
                      ? 'border-primary-500 bg-primary-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="dbType"
                      value="mysql"
                      checked={dbType === 'mysql'}
                      onChange={(e) => {
                        setDbType(e.target.value);
                        setConnectionTested(false);
                        setDbConfig({ ...dbConfig, port: '3306' });
                      }}
                      className="mt-1 mr-4"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 text-lg">MySQL</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Globe size={12} className="mr-1" /> Web Hosting
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">Use this if your web host gave you MySQL database details.</p>
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-sm font-medium text-gray-700 mb-2">Best for:</p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> cPanel/Plesk web hosting (GoDaddy, Bluehost, HostGator)</li>
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> WordPress hosting environments</li>
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> Shared hosting with MySQL included</li>
                        </ul>
                        <p className="text-xs text-amber-600 mt-2 flex items-center">
                          <AlertCircle size={12} className="mr-1" /> You'll need your database name, username, and password from your host
                        </p>
                      </div>
                    </div>
                  </div>
                </label>

                {/* PostgreSQL Option */}
                <label
                  className={`block p-5 border-2 rounded-xl cursor-pointer transition-all ${
                    dbType === 'postgresql' 
                      ? 'border-primary-500 bg-primary-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="dbType"
                      value="postgresql"
                      checked={dbType === 'postgresql'}
                      onChange={(e) => {
                        setDbType(e.target.value);
                        setConnectionTested(false);
                        setDbConfig({ ...dbConfig, port: '5432' });
                      }}
                      className="mt-1 mr-4"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 text-lg">PostgreSQL</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <Server size={12} className="mr-1" /> Advanced
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">For advanced users or if your host specifically provides PostgreSQL.</p>
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-sm font-medium text-gray-700 mb-2">Best for:</p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> Supabase, Neon, or other PostgreSQL providers</li>
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> AWS RDS, Google Cloud SQL</li>
                          <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2" /> Large businesses needing advanced features</li>
                        </ul>
                        <p className="text-xs text-amber-600 mt-2 flex items-center">
                          <AlertCircle size={12} className="mr-1" /> You'll need your database connection details from your provider
                        </p>
                      </div>
                    </div>
                  </div>
                </label>
              </div>

              {/* Database Configuration (for MySQL/PostgreSQL) */}
              {(dbType === 'mysql' || dbType === 'postgresql') && (
                <div className="mt-6 space-y-4">
                  {/* Expandable Help Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setShowHostingHelp(!showHostingHelp)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center">
                        <HelpCircle size={20} className="text-blue-600 mr-3" />
                        <div>
                          <span className="font-semibold text-blue-900">Need help finding your database details?</span>
                          <p className="text-sm text-blue-700">Click here for step-by-step instructions for your hosting provider</p>
                        </div>
                      </div>
                      {showHostingHelp ? <ChevronUp size={20} className="text-blue-600" /> : <ChevronDown size={20} className="text-blue-600" />}
                    </button>
                    
                    {showHostingHelp && (
                      <div className="border-t border-blue-200 p-4 bg-white">
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Select your hosting provider:</label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { id: 'hostinger', name: 'Hostinger' },
                              { id: 'godaddy', name: 'GoDaddy' },
                              { id: 'bluehost', name: 'Bluehost' },
                              { id: 'hostgator', name: 'HostGator' },
                              { id: 'siteground', name: 'SiteGround' },
                              { id: 'cpanel', name: 'Other (cPanel)' }
                            ].map(host => (
                              <button
                                key={host.id}
                                onClick={() => setSelectedHost(host.id)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                  selectedHost === host.id
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {host.name}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Hostinger Instructions */}
                        {selectedHost === 'hostinger' && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded mr-2">Hostinger</span>
                              How to find your database details
                            </h4>
                            <ol className="space-y-3 text-sm text-gray-700">
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">1</span>
                                <span>Log in to <strong>hPanel</strong> at <a href="https://hpanel.hostinger.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">hpanel.hostinger.com</a></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">2</span>
                                <span>Click <strong>"Websites"</strong> in the left sidebar</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">3</span>
                                <span>Find your website and click the <strong>"Dashboard"</strong> button next to it</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">4</span>
                                <span>In the website dashboard sidebar, click <strong>"Databases"</strong> (it will automatically open the Management page)</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">5</span>
                                <span>If no database exists, create one by entering a name and password, then click <strong>"Create"</strong></span>
                              </li>
                            </ol>
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800">
                                <strong>Important:</strong> You must first select a specific website to see the database options. They won't appear on the main hPanel home screen!
                              </p>
                            </div>
                            <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-700 mb-3">What to enter in the form below:</p>
                              <table className="w-full text-sm">
                                <tbody>
                                  <tr className="border-b bg-green-50">
                                    <td className="py-2 font-medium text-gray-600 w-28">Host / Server:</td>
                                    <td className="py-2 text-gray-900">
                                      <code className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded font-bold">localhost</code>
                                      <span className="text-xs text-gray-500 ml-2">(not shown in Hostinger - just use this value)</span>
                                    </td>
                                  </tr>
                                  <tr className="border-b bg-green-50">
                                    <td className="py-2 font-medium text-gray-600">Port:</td>
                                    <td className="py-2 text-gray-900">
                                      <code className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded font-bold">3306</code>
                                      <span className="text-xs text-gray-500 ml-2">(not shown in Hostinger - just use this value)</span>
                                    </td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Database Name:</td>
                                    <td className="py-2 text-gray-900">Copy from <strong>"MySQL Database"</strong> column (e.g., <code className="bg-gray-100 px-1 rounded">u745879360_mydb</code>)</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Username:</td>
                                    <td className="py-2 text-gray-900">Copy from <strong>"MySQL User"</strong> column (e.g., <code className="bg-gray-100 px-1 rounded">u745879360_user</code>)</td>
                                  </tr>
                                  <tr>
                                    <td className="py-2 font-medium text-gray-600">Password:</td>
                                    <td className="py-2 text-gray-900">The password you set when creating (click ⋮ → "Change Password" if forgotten)</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 flex items-center">
                              <ExternalLink size={12} className="mr-1" />
                              <a href="https://www.hostinger.com/support/1583552-how-to-find-your-mysql-database-details" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Official Hostinger Guide: Finding MySQL Database Details
                              </a>
                            </p>
                          </div>
                        )}
                        
                        {/* GoDaddy Instructions */}
                        {selectedHost === 'godaddy' && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <span className="bg-teal-600 text-white text-xs px-2 py-0.5 rounded mr-2">GoDaddy</span>
                              How to find your database details
                            </h4>
                            <ol className="space-y-3 text-sm text-gray-700">
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">1</span>
                                <span>Go to your <a href="https://account.godaddy.com/products" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">GoDaddy Products page</a></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">2</span>
                                <span>Under <strong>"Web Hosting"</strong>, find your hosting account and click <strong>"Manage"</strong></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">3</span>
                                <span>In the account Dashboard, click <strong>"cPanel Admin"</strong></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">4</span>
                                <span>In cPanel, find the <strong>"Databases"</strong> section and click <strong>"MySQL Database Wizard"</strong></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">5</span>
                                <span>Follow the wizard to create a database, user, and assign privileges</span>
                              </li>
                            </ol>
                            <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">Your details will be:</p>
                              <table className="w-full text-sm">
                                <tbody>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600 w-28">Host / Server:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">localhost</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Port:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">3306</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Database Name:</td>
                                    <td className="py-2 text-gray-900">cPanel username + name (e.g., <code className="bg-gray-100 px-1 rounded">abc123_grooming</code>)</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Username:</td>
                                    <td className="py-2 text-gray-900">cPanel username + user (e.g., <code className="bg-gray-100 px-1 rounded">abc123_dbuser</code>)</td>
                                  </tr>
                                  <tr>
                                    <td className="py-2 font-medium text-gray-600">Password:</td>
                                    <td className="py-2 text-gray-900">The password you set in the wizard</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 flex items-center">
                              <ExternalLink size={12} className="mr-1" />
                              <a href="https://www.godaddy.com/help/create-a-mysql-database-in-my-web-hosting-cpanel-16016" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Official GoDaddy Guide: Create MySQL Database
                              </a>
                            </p>
                          </div>
                        )}
                        
                        {/* Bluehost Instructions */}
                        {selectedHost === 'bluehost' && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <span className="bg-blue-700 text-white text-xs px-2 py-0.5 rounded mr-2">Bluehost</span>
                              How to find your database details
                            </h4>
                            <ol className="space-y-3 text-sm text-gray-700">
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">1</span>
                                <span>Log in to your <a href="https://my.bluehost.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Bluehost Account Manager</a></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">2</span>
                                <span>Click <strong>"Websites"</strong> in the left menu</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">3</span>
                                <span>Click the <strong>"Manage"</strong> button next to your website</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">4</span>
                                <span>On the Overview tab, click <strong>"cPanel"</strong> to open cPanel in a new tab</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">5</span>
                                <span>In cPanel, find <strong>"Databases"</strong> section and click <strong>"MySQL Databases"</strong></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">6</span>
                                <span>Create a database, create a user, then add the user to the database with "All Privileges"</span>
                              </li>
                            </ol>
                            <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">Your details will be:</p>
                              <table className="w-full text-sm">
                                <tbody>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600 w-28">Host / Server:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">localhost</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Port:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">3306</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Database Name:</td>
                                    <td className="py-2 text-gray-900">Shown in "Current Databases" list after creation</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Username:</td>
                                    <td className="py-2 text-gray-900">The database user you created and added</td>
                                  </tr>
                                  <tr>
                                    <td className="py-2 font-medium text-gray-600">Password:</td>
                                    <td className="py-2 text-gray-900">The password you set for that user</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 flex items-center">
                              <ExternalLink size={12} className="mr-1" />
                              <a href="https://www.bluehost.com/help/article/how-to-create-and-delete-mysql-databases-and-users" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Official Bluehost Guide: MySQL Databases
                              </a>
                            </p>
                          </div>
                        )}
                        
                        {/* HostGator Instructions */}
                        {selectedHost === 'hostgator' && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded mr-2">HostGator</span>
                              How to find your database details
                            </h4>
                            <ol className="space-y-3 text-sm text-gray-700">
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">1</span>
                                <span>Log in to your <a href="https://portal.hostgator.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">HostGator Customer Portal</a></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">2</span>
                                <span>Find your hosting package and click <strong>"cPanel"</strong> to open your control panel</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">3</span>
                                <span>In cPanel, find the <strong>"Databases"</strong> section</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">4</span>
                                <span>Click <strong>"MySQL Database Wizard"</strong> to create a new database step-by-step</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">5</span>
                                <span>Follow the wizard: name your database, create a user with password, then assign "All Privileges"</span>
                              </li>
                            </ol>
                            <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">Your details will be:</p>
                              <table className="w-full text-sm">
                                <tbody>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600 w-28">Host / Server:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">localhost</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Port:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">3306</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Database Name:</td>
                                    <td className="py-2 text-gray-900">cPanel prefix + name (e.g., <code className="bg-gray-100 px-1 rounded">youruser_grooming</code>)</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Username:</td>
                                    <td className="py-2 text-gray-900">cPanel prefix + user (e.g., <code className="bg-gray-100 px-1 rounded">youruser_dbuser</code>)</td>
                                  </tr>
                                  <tr>
                                    <td className="py-2 font-medium text-gray-600">Password:</td>
                                    <td className="py-2 text-gray-900">The password you set in the wizard</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 flex items-center">
                              <ExternalLink size={12} className="mr-1" />
                              <a href="https://www.hostgator.com/help/article/how-do-i-create-a-mysql-database-a-user-and-then-delete-if-needed" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Official HostGator Guide: Create MySQL Database
                              </a>
                            </p>
                          </div>
                        )}
                        
                        {/* SiteGround Instructions */}
                        {selectedHost === 'siteground' && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded mr-2">SiteGround</span>
                              How to find your database details
                            </h4>
                            <ol className="space-y-3 text-sm text-gray-700">
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">1</span>
                                <span>Log in to your <a href="https://my.siteground.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">SiteGround Client Area</a></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">2</span>
                                <span>Go to <strong>"Websites"</strong> and click <strong>"Site Tools"</strong> for your website</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">3</span>
                                <span>In Site Tools, navigate to <strong>"Site" → "MySQL"</strong> in the left menu</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">4</span>
                                <span>Click <strong>"Databases"</strong> tab to see existing databases or create a new one</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">5</span>
                                <span>Your database details will be shown in the list (check the label to identify your database)</span>
                              </li>
                            </ol>
                            <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">Your details will be:</p>
                              <table className="w-full text-sm">
                                <tbody>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600 w-28">Host / Server:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">localhost</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Port:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">3306</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Database Name:</td>
                                    <td className="py-2 text-gray-900">Shown in the Databases list</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Username:</td>
                                    <td className="py-2 text-gray-900">The user associated with the database</td>
                                  </tr>
                                  <tr>
                                    <td className="py-2 font-medium text-gray-600">Password:</td>
                                    <td className="py-2 text-gray-900">The password you set when creating the database</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 flex items-center">
                              <ExternalLink size={12} className="mr-1" />
                              <a href="https://www.siteground.com/kb/access-mysql-database/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Official SiteGround Guide: Access MySQL Database
                              </a>
                            </p>
                          </div>
                        )}
                        
                        {/* Generic cPanel Instructions */}
                        {selectedHost === 'cpanel' && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <span className="bg-gray-700 text-white text-xs px-2 py-0.5 rounded mr-2">cPanel</span>
                              General cPanel Instructions
                            </h4>
                            <ol className="space-y-3 text-sm text-gray-700">
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">1</span>
                                <span>Log in to your <strong>cPanel</strong> (usually at yourdomain.com/cpanel or yourdomain.com:2083)</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">2</span>
                                <span>Find the <strong>"Databases"</strong> section and click <strong>"MySQL Databases"</strong></span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">3</span>
                                <span><strong>Create a new database:</strong> Enter a name and click "Create Database"</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">4</span>
                                <span><strong>Create a new user:</strong> Scroll down, enter username and password, click "Create User"</span>
                              </li>
                              <li className="flex">
                                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">5</span>
                                <span><strong>Add user to database:</strong> Select the user and database, click "Add", then grant "All Privileges"</span>
                              </li>
                            </ol>
                            <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3">
                              <table className="w-full text-sm">
                                <tbody>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Host / Server:</td>
                                    <td className="py-2 text-gray-900">Almost always <code className="bg-gray-100 px-1 rounded">localhost</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Port:</td>
                                    <td className="py-2 text-gray-900"><code className="bg-gray-100 px-1 rounded">3306</code> (default, usually don't need to change)</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Database Name:</td>
                                    <td className="py-2 text-gray-900">Usually <code className="bg-gray-100 px-1 rounded">cpanelusername_dbname</code></td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="py-2 font-medium text-gray-600">Username:</td>
                                    <td className="py-2 text-gray-900">Usually <code className="bg-gray-100 px-1 rounded">cpanelusername_dbuser</code></td>
                                  </tr>
                                  <tr>
                                    <td className="py-2 font-medium text-gray-600">Password:</td>
                                    <td className="py-2 text-gray-900">The password you created for the database user</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800">
                                <strong>Important:</strong> In cPanel, database names and usernames are usually prefixed with your cPanel username followed by an underscore. For example, if your cPanel username is "john" and you create a database called "grooming", the full database name will be "john_grooming".
                              </p>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 flex items-center">
                              <ExternalLink size={12} className="mr-1" />
                              <a href="https://docs.cpanel.net/cpanel/databases/mysql-database-wizard/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Official cPanel Documentation: MySQL Database Wizard
                              </a>
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Database Details Form */}
                  <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <Settings size={18} className="mr-2 text-amber-600" />
                        Enter Your Database Details
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Enter the details from your hosting provider below. If you're not sure, click the help section above.
                      </p>
                    </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Host / Server Address
                        <span className="text-gray-400 font-normal ml-1">(e.g., localhost or db.example.com)</span>
                      </label>
                      <input
                        type="text"
                        value={dbConfig.host}
                        onChange={(e) => setDbConfig({ ...dbConfig, host: e.target.value })}
                        className="input"
                        placeholder="localhost"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Port
                        <span className="text-gray-400 font-normal ml-1">(usually {dbType === 'mysql' ? '3306' : '5432'})</span>
                      </label>
                      <input
                        type="text"
                        value={dbConfig.port}
                        onChange={(e) => setDbConfig({ ...dbConfig, port: e.target.value })}
                        className="input"
                        placeholder={dbType === 'mysql' ? '3306' : '5432'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Database Name
                        <span className="text-gray-400 font-normal ml-1">(the name you created)</span>
                      </label>
                      <input
                        type="text"
                        value={dbConfig.database}
                        onChange={(e) => setDbConfig({ ...dbConfig, database: e.target.value })}
                        className="input"
                        placeholder="dog_grooming"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                        <span className="text-gray-400 font-normal ml-1">(database user)</span>
                      </label>
                      <input
                        type="text"
                        value={dbConfig.user}
                        onChange={(e) => setDbConfig({ ...dbConfig, user: e.target.value })}
                        className="input"
                        placeholder={dbType === 'mysql' ? 'your_db_user' : 'postgres'}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                        <span className="text-gray-400 font-normal ml-1">(database password)</span>
                      </label>
                      <input
                        type="password"
                        value={dbConfig.password}
                        onChange={(e) => setDbConfig({ ...dbConfig, password: e.target.value })}
                        className="input"
                        placeholder="Enter your database password"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3 pt-2">
                    {/* Connection Test Result Feedback */}
                    {error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                        <div className="flex items-start">
                          <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium">Connection Failed</p>
                            <p className="text-sm">{error}</p>
                          </div>
                        </div>
                        {error.includes('ECONNREFUSED') && (
                          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
                            <p className="font-medium">Running locally?</p>
                            <p className="mt-1">If you're testing this app on your computer, you can't connect to your hosting provider's database remotely. The connection will only work once the app is deployed to your hosting server (e.g., Hostinger).</p>
                            <p className="mt-2">For local testing, you can either:</p>
                            <ul className="list-disc ml-5 mt-1">
                              <li>Use SQLite (no setup required)</li>
                              <li>Install MySQL locally on your computer</li>
                              <li>Skip the test and deploy to your hosting first</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {connectionTested && !error && (
                      <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                        <CheckCircle size={20} className="mr-2 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Connection Successful!</p>
                          <p className="text-sm">Your database credentials are correct. You can proceed to the next step.</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">
                        {!connectionTested && !error && "Test your connection before continuing"}
                      </p>
                      <button
                        onClick={testConnection}
                        disabled={loading}
                        className="btn btn-secondary flex items-center"
                      >
                        {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                        {connectionTested ? 'Test Again' : 'Test Connection'}
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Import Config (optional) */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Quick Setup from Demo</h2>
                <p className="text-gray-600 mt-2">
                  If you customized your settings on the demo site, upload your saved config file to pre-load your services, pricing, hours, and branding automatically.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <strong>How to get your config file:</strong> In the demo or any MorphBook installation, go to Admin → Settings → System Information → click <strong>Export Config</strong>.
              </div>

              {!configImported ? (
                <div className="space-y-4">
                  <label
                    className={`block border-2 rounded-xl p-5 cursor-pointer transition-all ${
                      configFile ? 'border-primary-500 bg-primary-50' : 'border-dashed border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files[0];
                        if (f) { setConfigFile(f); setError(''); }
                      }}
                    />
                    <div className="flex items-center space-x-4">
                      <Upload size={32} className="text-gray-400 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">{configFile ? configFile.name : 'Click to upload config file'}</p>
                        <p className="text-sm text-gray-500 mt-1">JSON file exported from MorphBook</p>
                      </div>
                    </div>
                  </label>

                  {configFile && (
                    <button
                      onClick={importConfig}
                      disabled={configImportLoading}
                      className="btn btn-primary w-full"
                    >
                      {configImportLoading ? <><Loader2 className="animate-spin mr-2" size={18} />Importing...</> : 'Apply Configuration'}
                    </button>
                  )}

                  <p className="text-center text-sm text-gray-500">
                    Don't have a config file? No problem — just click <strong>Continue</strong> to set everything up manually.
                  </p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                  <CheckCircle size={48} className="mx-auto mb-3 text-green-500" />
                  <p className="font-semibold text-green-800">Configuration Applied!</p>
                  <p className="text-sm text-green-600 mt-1">
                    Imported: {configImportResult?.imported?.join(', ')}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">Your services, pricing, hours, and branding are ready. Continue to create your admin account.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Your Clients - Combined setup and import */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {!showMappingUI ? (
                <>
                  <h2 className="text-xl font-semibold text-gray-900">Set Up Your Client List</h2>
                  <p className="text-gray-600">
                    Do you have existing clients you'd like to bring into MorphBook, or are you starting fresh?
                  </p>
                  
                  {!(importProgress?.complete) && (
                    <div className="space-y-4">
                      {/* Option 1: Import existing clients */}
                      <div 
                        className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
                          importFile ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => document.getElementById('import-file-input').click()}
                      >
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setImportFile(file);
                              setImportSkipped(false);
                              parseCSVFile(file);
                            }
                          }}
                          className="hidden"
                          id="import-file-input"
                        />
                        <div className="flex items-start">
                          <div className="text-3xl mr-4">📋</div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">I have existing client data</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Upload a spreadsheet (CSV or Excel) with your current clients and pets
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Option 2: Start fresh */}
                      <div 
                        className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
                          importSkipped ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => { setImportFile(null); setImportSkipped(true); setShowMappingUI(false); }}
                      >
                        <div className="flex items-start">
                          <div className="text-3xl mr-4">✨</div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">I'm starting fresh</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Set up an empty system - you'll add clients as they book appointments
                            </p>
                            {importSkipped && (
                              <div className="mt-2 flex items-center text-primary-600">
                                <CheckCircle size={16} className="mr-1" />
                                <span className="text-sm font-medium">Selected</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Column Mapping UI */}
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">Map Your Data</h2>
                    <button 
                      onClick={() => { setShowMappingUI(false); setImportFile(null); setCsvColumns([]); }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      ← Choose different file
                    </button>
                  </div>
                  <p className="text-gray-600">
                    We found <strong>{csvColumns.length} columns</strong> in your file. Tell us what each column contains so we can create your client profiles correctly.
                  </p>

                  {/* File info */}
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center">
                    <span className="text-2xl mr-3">📄</span>
                    <div>
                      <p className="font-medium text-gray-900">{importFile?.name}</p>
                      <p className="text-xs text-gray-500">{csvPreviewData.length} preview rows loaded</p>
                    </div>
                  </div>

                  {/* Column mapping table */}
                  <div className="border rounded-xl overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 border-b">
                      <div className="grid grid-cols-2 gap-4 text-sm font-medium text-gray-700">
                        <span>Your Column</span>
                        <span>Maps To</span>
                      </div>
                    </div>
                    <div className="divide-y max-h-64 overflow-y-auto">
                      {csvColumns.map((col, idx) => (
                        <div key={idx} className="px-4 py-3 grid grid-cols-2 gap-4 items-center hover:bg-gray-50">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{col}</p>
                            {csvPreviewData[0] && (
                              <p className="text-xs text-gray-500 truncate">e.g., "{csvPreviewData[0][col]}"</p>
                            )}
                          </div>
                          <select
                            value={columnMapping[col] || 'notes'}
                            onChange={(e) => setColumnMapping({ ...columnMapping, [col]: e.target.value })}
                            className="input text-sm py-1.5"
                          >
                            <optgroup label="Client Info">
                              {morphBookFields.filter(f => f.category === 'client').map(f => (
                                <option key={f.id} value={f.id}>{f.label}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Pet Info">
                              {morphBookFields.filter(f => f.category === 'pet').map(f => (
                                <option key={f.id} value={f.id}>{f.label}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Other">
                              {morphBookFields.filter(f => f.category === 'other').map(f => (
                                <option key={f.id} value={f.id}>{f.label}</option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary of what will be created */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-2">What will be created:</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>✓ <strong>Client profiles</strong> with name, email, phone, address</li>
                      <li>✓ <strong>Pet profiles</strong> linked to each client</li>
                      <li>✓ Any unmapped data goes into the <strong>Notes</strong> field</li>
                    </ul>
                  </div>
                </>
              )}

              {/* Setup button - shown when not yet created */}
              {!tablesCreated && (importFile || importSkipped) && (
                <div className="pt-2">
                  <button
                    onClick={async () => {
                      // First create tables
                      const created = await runMigrations();
                      if (!created) return;
                      
                      // Then import if file was uploaded
                      if (importFile && allCsvData.length > 0) {
                        await importClients();
                      }
                    }}
                    disabled={loading}
                    className="btn btn-primary w-full text-lg py-3"
                  >
                    {loading ? <Loader2 className="animate-spin mr-2" size={20} /> : null}
                    {loading ? 'Setting up...' : showMappingUI ? `Create Profiles & Import ${allCsvData.length} Clients` : 'Set Up My System'}
                  </button>
                </div>
              )}

              {/* Success state */}
              {tablesCreated && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                  <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
                  <p className="font-medium text-green-800">Your system is ready!</p>
                  {importProgress?.complete && importProgress?.imported > 0 ? (
                    <div className="text-sm text-green-600 mt-1">
                      <p>Successfully imported <strong>{importProgress.imported} clients</strong> and <strong>{importProgress.pets || 0} pets</strong>!</p>
                      {importProgress.errors?.length > 0 && (
                        <p className="text-amber-600 mt-2">({importProgress.errors.length} rows had issues)</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-green-600 mt-1">
                      {importSkipped ? "You can add clients anytime from your dashboard." : "Your client data has been set up."}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Admin Account */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Create Your Account</h2>
              <p className="text-gray-600">
                This will be your login to manage appointments, clients, and your business settings.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                  <input
                    type="text"
                    value={adminData.businessName}
                    onChange={(e) => setAdminData({ ...adminData, businessName: e.target.value })}
                    className="input"
                    placeholder="e.g. Pawfect Pups Grooming"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={adminData.firstName}
                      onChange={(e) => setAdminData({ ...adminData, firstName: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={adminData.lastName}
                      onChange={(e) => setAdminData({ ...adminData, lastName: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={adminData.email}
                    onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                    className="input"
                    placeholder="you@yourbusiness.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={adminData.phone}
                    onChange={(e) => setAdminData({ ...adminData, phone: e.target.value })}
                    className="input"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <input
                      type="password"
                      value={adminData.password}
                      onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                      className="input"
                      placeholder="At least 6 characters"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                    <input
                      type="password"
                      value={adminData.confirmPassword}
                      onChange={(e) => setAdminData({ ...adminData, confirmPassword: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {currentStep === 5 && (
            <div className="text-center space-y-6">
              <div className="text-green-500">
                <CheckCircle size={64} className="mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">You're All Set!</h2>
              <p className="text-gray-600">
                Your MorphBook booking system is ready to go. Click below to log in
                and start managing your business.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                <h3 className="font-medium text-green-800 mb-2">What's Next?</h3>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Set up your business hours and services</li>
                  <li>• Add your team members/groomers</li>
                  <li>• Customize your booking page</li>
                  <li>• Share your booking link with clients!</li>
                </ul>
              </div>
              <p className="text-sm text-gray-500">
                Want to add a booking button to your existing website? Continue to see embed options.
              </p>
            </div>
          )}

          {/* Step 6: Embed on Your Website */}
          {currentStep === 6 && <EmbedStep />}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          {currentStep > 1 && currentStep < 6 ? (
            <button
              onClick={handleBack}
              className="btn btn-secondary flex items-center"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back
            </button>
          ) : (
            <div />
          )}
          
          <button
            onClick={handleNext}
            disabled={loading}
            className="btn btn-primary flex items-center"
          >
            {loading && <Loader2 className="animate-spin mr-2" size={18} />}
            {currentStep === 6 ? 'Go to Login' : 'Continue'}
            {currentStep < 6 && <ArrowRight size={18} className="ml-2" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
